// @flow
import {HA_SERP_MIN_MAX_PRICE_FIELDS} from '../abacus/experiments';

export const SEARCHPARAMS_SET = 'SEARCH_PARAMS/SET';
export const SEARCHPARAMS_INIT_Q = 'SEARCH_PARAMS/INIT_Q';
export const SEARCHPARAMS_SYNC_INPUT_PARAMS = 'SEARCH_PARAMS/SYNC_INPUT_PARAMS';
export const SEARCHPARAMS_UPDATE_INPUT_PARAMS_REQUEST =
    'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_REQUEST';
export const SEARCHPARAMS_UPDATE_INPUT_PARAMS_COMPLETE =
    'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_COMPLETE';
export const SEARCHPARAMS_RESET_INPUT_PARAMS =
    'SEARCH_PARAMS/RESET_INPUT_PARAMS';
export const SEARCHPARAMS_UPDATE_PRICE_INPUT_VALUES_VALIDITY =
    'SEARCH_PARAMS/UPDATE_PRICE_INPUT_VALUES_VALIDITY';

export type ActionType =
    | 'SEARCH_PARAMS/INIT_Q'
    | 'SEARCH_PARAMS/SET'
    | 'SEARCH_PARAMS/SYNC_INPUT_PARAMS'
    | 'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_REQUEST'
    | 'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_COMPLETE'
    | 'SEARCH_PARAMS/RESET_INPUT_PARAMS'
    | 'SEARCH_PARAMS/UPDATE_PRICE_INPUT_VALUES_VALIDITY';
export type Action = {
    type: ActionType,
    payload: any
};

import {newArrayFromFilters} from '../utils/filters';
import {syncPets, SYNC_STRATEGY_FILTER} from '@homeaway/search-url-parser';

import type {State as ReduxState} from '../reducers/index';
import type {SearchParam, SearchParamsType} from '../reducers/searchParams';

let setDebouncedFiltersTimeout = null;

const dispatchUpdateInputParams = (
    params: SearchParamsType,
    state: ReduxState,
    dispatch: (Action) => any
) => {
    // If filter is undefined (not set), the current
    // filters will be preserved. Otherwise they'll be
    // overridden with the filters in payload.
    const filter =
        typeof params.filter === 'undefined'
            ? newArrayFromFilters(state.searchParams.inputParams)
            : // ensure `filter` property is re-written
              // unless explictitly `undefined`.
              newArrayFromFilters(params);

    // sync petIncluded with pet filter
    const newParams = syncPets({...params, filter}, SYNC_STRATEGY_FILTER);

    dispatch({
        type: SEARCHPARAMS_UPDATE_INPUT_PARAMS_REQUEST,
        payload: newParams
    });

    if (setDebouncedFiltersTimeout) {
        clearTimeout(setDebouncedFiltersTimeout);
    }

    setDebouncedFiltersTimeout = setTimeout(() => {
        dispatch({
            type: SEARCHPARAMS_UPDATE_INPUT_PARAMS_COMPLETE,
            payload: {}
        });
    }, 200);
};

const ACTION_CREATORS = {
    initQ(searchTerm: string): Action {
        return {
            type: SEARCHPARAMS_INIT_Q,
            payload: searchTerm
        };
    },

    /**
     * setSearchParams()
     * This enforce search params due to conditions and set `inputParams``
     */
    setSearchParams(payload: {pathname: string, params: any}) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {
                abacus,
                system: {currency}
            } = getState();
            const isMinMaxPriceFieldsTestV2 =
                (abacus[HA_SERP_MIN_MAX_PRICE_FIELDS] || {}).value === 2;

            dispatch({
                type: SEARCHPARAMS_SET,
                payload: {
                    ...payload,
                    currency,
                    isMinMaxPriceFieldsTestV2
                }
            });
        };
    },

    /**
     * syncInputParams()
     * This syncs our `inputParams` by copying our current `params` into `inputParams`
     */
    syncInputParams(response: SearchParamsType = {}): Function {
        return (dispatch: (Action) => any) => {
            dispatch({
                type: SEARCHPARAMS_SYNC_INPUT_PARAMS,
                payload: response
            });
        };
    },

    /**
     * resetInputParams()
     * This will reset inputParams to an empty default state.
     */
    resetInputParams(payload?: SearchParam | SearchParam[]): Function {
        return (dispatch: (Action) => any) => {
            dispatch({
                type: SEARCHPARAMS_RESET_INPUT_PARAMS,
                payload
            });
        };
    },

    /**
     * updateInputParams()
     * This mutates `inputParams`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     */
    updateInputParams(params: SearchParamsType): Function {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            dispatchUpdateInputParams(params, getState(), dispatch);
        };
    },

    /**
     * updateFilterCheck()
     * This mutates `inputParams.filter`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     */
    updateFilterCheck(updatedFilter: {checked: boolean, name: string}) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {filter} = getState().searchParams.inputParams;
            const filterArray: string[] = [].concat(filter || []);
            const newParams: SearchParamsType = {
                filter: filterArray
            };

            if (updatedFilter.checked) {
                filterArray.push(updatedFilter.name);
            } else {
                const elementIndex = filterArray.indexOf(updatedFilter.name);
                if (elementIndex >= 0) {
                    filterArray.splice(elementIndex, 1);
                }
            }

            dispatchUpdateInputParams(newParams, getState(), dispatch);
        };
    },

    updatePriceInputValuesValidity(areValid: boolean): Action {
        return {
            type: SEARCHPARAMS_UPDATE_PRICE_INPUT_VALUES_VALIDITY,
            payload: {areValid}
        };
    }
};

export default {
    ...ACTION_CREATORS
};
